// src/components/finance/FinanceVat.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { fetchFinanceVatReport, exportFinance } from '../../api.js'
import FinanceNav from './FinanceNav.jsx'

function isoDate(d) {
  const dt = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toISOString().slice(0, 10)
}
function fmtDateFR(d) {
  if (!d) return '—'
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return '—'
  return dt.toLocaleDateString('fr-FR')
}
function fmtMoney(v) {
  const n = Number(v || 0)
  return `${n.toLocaleString('fr-FR')} XOF`
}

export default function FinanceVat() {
  const defaultRange = useMemo(() => {
    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth(), 1)
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return { from: isoDate(from), to: isoDate(to) }
  }, [])

  const [from, setFrom] = useState(defaultRange.from)
  const [to, setTo] = useState(defaultRange.to)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const res = await fetchFinanceVatReport({ from, to })
      setData(res)
    } catch (e) {
      setErr(String(e?.message || e || 'Erreur rapport TVA'))
    } finally {
      setLoading(false)
    }
  }, [from, to])

  useEffect(() => { load() }, [load])

  const vatDue = Number(data?.vatDue || 0)

  return (
    <div className="p-6 space-y-6 min-h-screen">
      <div className="flex flex-wrap gap-3 items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rapport TVA</h1>
          <div className="text-sm text-slate-500 mt-1">
            Période: <span className="font-semibold">{fmtDateFR(from)}</span> → <span className="font-semibold">{fmtDateFR(to)}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => exportFinance({ from, to, type: 'journal', format: 'csv' })}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            disabled={loading}
          >
            🧾 Export CSV
          </button>
        </div>
      </div>

      <FinanceNav />

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Du</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Au</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2" />
          </div>
          <div>
            <button type="button" className="px-4 py-2 rounded-lg bg-slate-900 text-white w-full" onClick={load} disabled={loading}>
              {loading ? '…' : '🔄'}
            </button>
          </div>
        </div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-xs text-slate-500 font-semibold mb-1">TVA collectée (44571)</div>
          <div className="text-xl font-bold text-slate-900">{fmtMoney(data?.vatCollected)}</div>
          <div className="text-xs text-slate-400 mt-1">Sur ventes et prestations</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-xs text-slate-500 font-semibold mb-1">TVA déductible (44566)</div>
          <div className="text-xl font-bold text-slate-900">{fmtMoney(data?.vatDeductible)}</div>
          <div className="text-xs text-slate-400 mt-1">Sur achats fournisseurs</div>
        </div>
        <div className={`rounded-xl border p-5 ${vatDue >= 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
          <div className="text-xs font-semibold mb-1">TVA à reverser</div>
          <div className={`text-xl font-bold ${vatDue >= 0 ? 'text-amber-800' : 'text-emerald-800'}`}>
            {fmtMoney(vatDue)}
          </div>
          <div className="text-xs text-slate-500 mt-1">= Collectée − Déductible</div>
        </div>
      </div>

      {data?.rows?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 text-xs text-slate-500 font-semibold">
            <div className="col-span-2">Code</div>
            <div className="col-span-5">Libellé</div>
            <div className="col-span-2 text-right">Débit</div>
            <div className="col-span-3 text-right">Crédit</div>
          </div>
          <div className="divide-y divide-slate-100">
            {data.rows.map((r, i) => (
              <div key={r.code || i} className="grid grid-cols-12 gap-3 px-4 py-3">
                <div className="col-span-2 font-semibold text-slate-900">{r.code}</div>
                <div className="col-span-5 text-sm text-slate-700">{r.label}</div>
                <div className="col-span-2 text-right text-sm">{fmtMoney(r.debit)}</div>
                <div className="col-span-3 text-right text-sm">{fmtMoney(r.credit)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && !err && !data && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-slate-500">
          Aucune donnée TVA sur la période.
        </div>
      )}
    </div>
  )
}
